/**
 * ImpersonationContext — admin org-impersonation state.
 *
 * Persists `{orgId, orgName}` in `sessionStorage` under the key
 * `impersonation` — the SAME key/shape `getImpersonationHeaders()`
 * (`../services/impersonationHeaders.ts`) reads, so the axios interceptor
 * and any raw fetch/EventSource call site pick up the active org without
 * depending on React context. Initial state is hydrated FROM
 * `sessionStorage` (not just written to it) so impersonation survives a
 * tab reload — sessionStorage (not localStorage) is deliberate: a new tab
 * should start un-impersonated.
 */
import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { purgeAllChatSnapshots } from '../utils/chatHistoryStore';

const STORAGE_KEY = 'impersonation';

export interface ImpersonatedOrg {
  orgId: number;
  orgName: string;
}

interface ImpersonationContextType {
  orgId: number | null;
  orgName: string | null;
  isImpersonating: boolean;
  start: (org: ImpersonatedOrg) => void;
  exit: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

function readStoredOrg(): ImpersonatedOrg | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.orgId === 'number' &&
      typeof parsed.orgName === 'string'
    ) {
      return { orgId: parsed.orgId, orgName: parsed.orgName };
    }
    return null;
  } catch {
    return null;
  }
}

interface ImpersonationProviderProps {
  children: ReactNode;
}

export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({ children }) => {
  const [org, setOrg] = useState<ImpersonatedOrg | null>(() => readStoredOrg());

  const start = useCallback((newOrg: ImpersonatedOrg) => {
    // CEPI conversations stored so far were produced under the PREVIOUS org.
    // Their keys already carry that org, so they can't be restored under the
    // new one — but they are still that org's data, so don't leave them at
    // rest either. start() does not reload, so nothing else would clear them.
    purgeAllChatSnapshots();
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newOrg));
    } catch {
      // sessionStorage may be unavailable (private mode / quota) — state
      // still updates in-memory for this tab, it just won't survive reload.
    }
    setOrg(newOrg);
  }, []);

  const exit = useCallback(() => {
    // The reload below does NOT clear sessionStorage, so on its own it would
    // leave the impersonated org's stored CEPI conversations behind.
    purgeAllChatSnapshots();
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // no-op — nothing to clean up if storage is unavailable
    }
    setOrg(null);
    // Impersonation changes the effective org for every data view (the
    // header changes what the backend's RLS scope resolves to), so a full
    // reload is the simplest way to guarantee no stale, wrong-org data is
    // left behind in component state or in-memory caches.
    window.location.reload();
  }, []);

  const value: ImpersonationContextType = {
    orgId: org ? org.orgId : null,
    orgName: org ? org.orgName : null,
    isImpersonating: org !== null,
    start,
    exit,
  };

  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
};

export const useImpersonation = (): ImpersonationContextType => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

export default ImpersonationContext;
